import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { IChoosenExperiencesInterface } from 'app/library/all-users/interfaces/choosen-experiences.interface';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { UNASSIGNED_EXPERIENCE_FILTER_ID, UNASSIGNED_EXPERIENCE_FILTER_TYPE } from 'app/library/experience/constants/unassigned-experience-filter.constant';
import { MaxBrainExperience } from 'app/library/experience/models/experience.model';
import { ExperienceService } from 'app/library/experience/services/experience.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-experience-filter',
    templateUrl: './experience-filter.component.html',
    styleUrls: ['./experience-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ExperienceFilterComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    preSelectUnassigned: boolean;

    @ViewChild('experienceTableScrollHost', { read: ElementRef })
    private _experienceTableScrollHost?: ElementRef<HTMLElement>;

    @Output()
    addedToList = new EventEmitter<IChoosenFilterInterface[]>();

    @Output()
    choosenExperiences = new EventEmitter<IChoosenExperiencesInterface>();

    dataSource: MatTableDataSource<MaxBrainExperience>;
    displayedColumns = ['name', 'buttons'];

    list: IChoosenFilterInterface[] = [];
    isTableActive = true;
    searchInput = new FormControl();
    isLoading = true;
    isUnassignedSelected: boolean;
    experiences: MaxBrainExperience[] = [];
    private _hadUnassignedChip = false;

    constructor(private _experienceService: ExperienceService, private _allUsersService: AllUsersApiService, private _translateService: TranslateService) {
        super();
    }

    async ngOnInit(): Promise<void> {
        await this._experienceService.fetchExperiences();

        this.isUnassignedSelected = this.preSelectUnassigned;
        this.isTableActive = !this.isUnassignedSelected;

        this._allUsersService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            this.list = filters;
            this._syncUnassignedSelectionFromList();
            this.updateDataSource();
        });

        if (this.isUnassignedSelected && !this._hasUnassignedExperienceFilter()) {
            this._applyUnassignedExperienceFilter(true);
        }

        this._experienceService.experience$
            .pipe(
                filter((experiences) => !!experiences),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((experiences) => {
                this.isLoading = false;
                this.experiences = experiences;
                this.updateDataSource();
            });
    }

    updateDataSource(): void {
        if (this.experiences.length) {
            this.dataSource = new MatTableDataSource(
                this.experiences.map((experience) => {
                    experience = Object.assign(new MaxBrainExperience(), experience, {
                        selected: this.list.some((item) => item.entity.id === experience.id && item.entity.type === 'Experience'),
                    });

                    return experience;
                })
            );
            this._scheduleScrollSelectedExperienceIntoView();
        }
    }

    private _scheduleScrollSelectedExperienceIntoView(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const host = this._experienceTableScrollHost?.nativeElement;
                const selectedEl = host?.querySelector<HTMLElement>('[data-experience-selected-row]');
                selectedEl?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            });
        });
    }

    addToList(experience: MaxBrainExperience): void {
        if (this.isUnassignedSelected || this._hasUnassignedExperienceFilter()) {
            this.isUnassignedSelected = false;
            this.isTableActive = true;
            this.list = this.list.filter((item) => item.entity.type !== UNASSIGNED_EXPERIENCE_FILTER_TYPE);
        }

        this.addedToList.emit([
            ...this.list.filter((item) => item.entity.type !== UNASSIGNED_EXPERIENCE_FILTER_TYPE),
            {
                entity: { id: experience.id, type: 'Experience' },
                valueToShow: `${this._translateService.instant('ENGAGE.EXPERIENCE.TITLE.SINGULAR')}: ${experience.name}`,
            },
        ]);
    }

    removeFromList(experience: MaxBrainExperience): void {
        this.addedToList.emit(this.list.filter((item) => !(item.entity.id === experience.id && item.entity.type === 'Experience')));
    }

    onSelection(): void {
        this.isTableActive = !this.isUnassignedSelected;

        if (this.isUnassignedSelected) {
            this._applyUnassignedExperienceFilter(true);
            this.choosenExperiences.emit({ experiences: [], isUnassignedSelected: true });
            return;
        }

        this._applyUnassignedExperienceFilter(false);
        this.choosenExperiences.emit({
            experiences: this.dataSource?.data.filter((exp) => exp.selected) || [],
            isUnassignedSelected: false,
        });
    }

    private _hasUnassignedExperienceFilter(): boolean {
        return this.list.some((item) => item.entity.type === UNASSIGNED_EXPERIENCE_FILTER_TYPE);
    }

    private _syncUnassignedSelectionFromList(): void {
        const hasUnassignedChip = this._hasUnassignedExperienceFilter();

        if (hasUnassignedChip) {
            this._hadUnassignedChip = true;
            this.isTableActive = false;

            if (!this.isUnassignedSelected) {
                this.isUnassignedSelected = true;
                this.choosenExperiences.emit({ experiences: [], isUnassignedSelected: true });
            }
            return;
        }

        if (this._hadUnassignedChip && this.isUnassignedSelected) {
            this._hadUnassignedChip = false;
            this.isUnassignedSelected = false;
            this.isTableActive = true;
            this.choosenExperiences.emit({
                experiences: this.dataSource?.data.filter((exp) => exp.selected) || [],
                isUnassignedSelected: false,
            });
            return;
        }

        this._hadUnassignedChip = false;
        this.isTableActive = !this.isUnassignedSelected;
    }

    private _applyUnassignedExperienceFilter(isSelected: boolean): void {
        if (isSelected) {
            const nonExperienceFilters = this.list.filter(
                (item) => item.entity.type !== 'Experience' && item.entity.type !== UNASSIGNED_EXPERIENCE_FILTER_TYPE
            );

            const updatedList: IChoosenFilterInterface[] = [
                ...nonExperienceFilters,
                {
                    entity: { id: UNASSIGNED_EXPERIENCE_FILTER_ID, type: UNASSIGNED_EXPERIENCE_FILTER_TYPE },
                    valueToShow: `${this._translateService.instant('ENGAGE.EXPERIENCE.TITLE.SINGULAR')}: ${this._translateService.instant(
                        'GENERAL.LABEL.UNASSIGNED_EXPERIENCE'
                    )}`,
                },
            ];

            this.list = updatedList;
            this.updateDataSource();
            this.addedToList.emit(updatedList);
            return;
        }

        if (!this._hasUnassignedExperienceFilter()) {
            return;
        }

        const updatedList = this.list.filter((item) => item.entity.type !== UNASSIGNED_EXPERIENCE_FILTER_TYPE);
        this.list = updatedList;
        this.addedToList.emit(updatedList);
    }
}
